import * as d3 from "d3";
import React, { useEffect, useRef } from "react";
import * as topojson from "topojson-client";
import { formatCurrency } from "../../utils";
const Map = ({
  handleStateReport,
  stateData,
  mutation,
  handleNavigate,
  localMutation,
}) => {
  const mapRef = useRef();
  useEffect(() => {
    d3.select(mapRef.current).select("svg").remove();

    // Create the SVG canvas
    const svg = d3
      .select(mapRef.current)
      .append("svg")
      .attr("viewBox", `0 0 960 600`) // Use viewBox for responsiveness
      .attr("preserveAspectRatio", "xMidYMid meet") // Maintain aspect ratio
      .style("width", "100%") // Full width for responsiveness
      .style("height", "auto"); // Auto height based on aspect ratio

    // Define the projection and path
    const projection = d3.geoAlbersUsa().translate([480, 300]).scale(1200);
    const path = d3.geoPath().projection(projection);

    // Add zoom functionality only for mobile screens
    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      const zoom = d3
        .zoom()
        .scaleExtent([1, 8]) // Set minimum and maximum zoom levels
        .on("zoom", (event) => {
          svg.selectAll("path").attr("transform", event.transform);
          svg.selectAll("text").attr("transform", event.transform);
          svg.selectAll("line").attr("transform", event.transform);
        });

      svg.call(zoom);
    }

    // Define state abbreviations and offsets
    const stateAbbreviations = {
      "01": "AL",
      "02": "AK",
      "04": "AZ",
      "05": "AR",
      "06": "CA",
      "08": "CO",
      "09": "CT",
      10: "DE",
      11: "DC",
      12: "FL",
      13: "GA",
      15: "HI",
      16: "ID",
      17: "IL",
      18: "IN",
      19: "IA",
      20: "KS",
      21: "KY",
      22: "LA",
      23: "ME",
      24: "MD",
      25: "MA",
      26: "MI",
      27: "MN",
      28: "MS",
      29: "MO",
      30: "MT",
      31: "NE",
      32: "NV",
      33: "NH",
      34: "NJ",
      35: "NM",
      36: "NY",
      37: "NC",
      38: "ND",
      39: "OH",
      40: "OK",
      41: "OR",
      42: "PA",
      44: "RI",
      45: "SC",
      46: "SD",
      47: "TN",
      48: "TX",
      49: "UT",
      50: "VT",
      51: "VA",
      53: "WA",
      54: "WV",
      55: "WI",
      56: "WY",
    };
    const annotationOffsets = {
      "09": [50, 20],
      10: [30, -10],
      11: [50, 20],
      15: [50, 0],
      24: [50, 5],
      25: [40, -10],
      33: [40, -5],
      34: [20, -5],
      44: [50, 0],
      50: [-50, -30],
    };

    d3.json("https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json").then(
      (data) => {
        const states = topojson.feature(data, data.objects.states).features;
        const tooltip = d3
          .select("#chart-container")
          .append("div")
          .attr("class", "tooltip desktop-only")
          .style("position", "absolute")
          .style("visibility", "hidden")
          .style("background", "white")
          // .style("padding", "8px")
          .style("border-radius", "4px")
          .style("box-shadow", "0px 2px 4px rgba(0,0,0,0.2)")
          .style("font-size", "12px")
          .style("z-index", "10"); // Ensure it's above other elements

        // Draw states
        svg
          .selectAll(".state")
          .data(states)
          .enter()
          .append("path")
          .attr("class", "state")
          .attr("d", path)
          .attr("fill", (d) => {
            const stateCode = stateAbbreviations[d.id];
            const stateName = stateData[stateCode] || "";
            return stateName ? stateName.color : "#c6c9d7";
          })
          .attr("stroke", "black")
          .attr("stroke-width", 1)
          .on("mouseover", function (event, d) {
            if (!d3.select(this).classed("clicked")) {
              const stateCode = stateAbbreviations[d.id];
              const stateName = stateData[stateCode] || "";
              if (stateName) {
                d3.select(this).attr("fill", "#AAAAAA");
              }
            }
            const tooltipWidth = tooltip.node().offsetWidth; // Tooltip width
            const tooltipHeight = tooltip.node().offsetHeight; // Tooltip height
            const viewportWidth = window.innerWidth; // Viewport width
            const viewportHeight = window.innerHeight; // Viewport height

            let top = event.pageY - 50; // Default top position
            let left = event.pageX + 10; // Default left position

            //console.log("top", top, "left", left);

            // Adjust for viewport boundaries
            if (top + tooltipHeight > viewportHeight) {
              top = event.pageY - tooltipHeight - 10;
            }
            if (left + tooltipWidth > viewportWidth) {
              left = event.pageX - tooltipWidth - 10;
            }

            const stateCode = stateAbbreviations[d.id];
            const stateName = stateData[stateCode] || "";

            if (stateName) {
              tooltip
                .html(
                  `
                <div style="border: 1px solid #ddd; max-width: 500px;">
                  <div style="background-color: #e5e9f2; color: #343a40; text-align: center; padding: 10px; font-weight: bold;">
                    ${d.properties.name} Average Saving
                  </div>
                  <div style="padding: 6px;">
                    <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                      <div style="flex: 1 1 calc(50% - 8px); border: 1px solid #ddd;">
                        <div style="background-color: #e5e9f2; color: #343a40; padding: 10px; font-weight: bold;">
                          Prepay
                        </div>
                        <div style="padding: 10px;">
                          <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>Average Saving:</div>
                            <div style="font-weight: bold;">${formatCurrency(
                    stateName?.prepayValues?.avgSaving
                  )}/gallon</div>
                          </div>
                          <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>Average Price:</div>
                            <div style="font-weight: bold;">${formatCurrency(
                    stateName?.prepayValues?.avgPrice
                  )}/gallon</div>
                          </div>
                        </div>
                      </div>
                      <div style="flex: 1 1 calc(50% - 8px); border: 1px solid #ddd;">
                        <div style="background-color: #e5e9f2; color: #343a40; padding: 10px; font-weight: bold;">
                          Credit
                        </div>
                        <div style="padding: 10px;">
                          <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>Average Saving:</div>
                            <div style="font-weight: bold;">${formatCurrency(
                    stateName?.creditValues?.avgSaving
                  )}/gallon</div>
                          </div>
                          <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div>Average Price:</div>
                            <div style="font-weight: bold;">${formatCurrency(
                    stateName?.creditValues?.avgPrice
                  )}/gallon</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              `
                )
                .style("visibility", "visible")
                .style("opacity", 1) // Ensure opacity for visibility
                .style("top", top + "px")
                .style("left", left + "px");
            } else {
              tooltip
                .html(
                  `<div style="border: 1px solid #ddd; max-width: 500px;">
                  <div style="background-color: #e5e9f2; color: #343a40; text-align: center; padding: 10px; font-weight: bold;">
                      ${d.properties.name}
                  </div>
              </div>
              <div>
                  <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                      <div>
                          <div style="display: flex; justify-content: center; align-items: center;width:" 400px">
                              <div class="alert alert-danger " style="border-radius:0">
                                  No data found for the selected filters. Please adjust your settings and try again.

                              </div>
                          </div>

                      </div>
                  </div>
              </div>`
                              )
                .style("visibility", "visible")
                .style("opacity", 1) // Ensure opacity for visibility
                .style("top", top + "px")
                .style("left", left + "px");
            }
          })
          .on("mousemove", function (event) {
            const tooltipWidth = tooltip.node().offsetWidth; // Tooltip width
            const tooltipHeight = tooltip.node().offsetHeight; // Tooltip height
            const viewportWidth = window.innerWidth; // Viewport width
            const viewportHeight = window.innerHeight; // Viewport height

            let top = event.pageY - 50; // Default top position
            let left = event.pageX + 10; // Default left position

            //console.log("top", top, "left", left);

            // Adjust for viewport boundaries
            if (top + tooltipHeight > viewportHeight) {
              top = event.pageY - tooltipHeight - 10;
            }
            if (left + tooltipWidth > viewportWidth) {
              left = event.pageX - tooltipWidth - 10;
            }

            tooltip.style("top", top + "px").style("left", left + "px");
          })
          .on("mouseout", function () {
            if (!d3.select(this).classed("clicked")) {
              d3.select(this).attr("fill", (d) => {
                const stateCode = stateAbbreviations[d.id];
                const stateName = stateData[stateCode] || "";
                return stateName ? stateName.color : "#c6c9d7";
              });
            }
            tooltip.style("visibility", "hidden");
          })
          .on("click", function (event, d) {
            const isClicked = d3.select(this).classed("clicked");
            const stateCode = stateAbbreviations[d.id];
            const stateName = stateData[stateCode] || "";

            if (stateName) {
              handleStateReport({
                state: d.properties.name,
                prepaySaving: stateName?.prepayValues?.avgSaving,
                prepayPrice: stateName?.prepayValues?.avgPrice,
                creditSaving: stateName?.creditValues?.avgSaving,
                creditPrice: stateName?.creditValues?.avgPrice,
              });
            }

            // Reset previously clicked states
            svg
              .selectAll(".state.clicked")
              .classed("clicked", false)
              .attr("fill", (d) => {
                const stateCode = stateAbbreviations[d.id];
                const stateName = stateData[stateCode] || "";
                return stateName ? stateName.color : "#c6c9d7";
              });

            if (isClicked) {
              d3.select(this)
                .classed("clicked", false)
                .attr("fill", (d) => {
                  handleStateReport(null);
                  const stateCode = stateAbbreviations[d.id];
                  const stateName = stateData[stateCode] || "";
                  return stateName ? stateName?.color : "#c6c9d7";
                });
            } else {
              if (stateName) {
                d3.select(this)
                  .classed("clicked", true)
                  .attr("fill", "#AAAAAA")
                  .attr("stroke", "black");
                handleNavigate();
              }
            }
          });

        // Add labels and annotations
        states.forEach((state) => {
          const centroid = path?.centroid(state);
          const stateId = state?.id?.padStart(2, "0");
          const abbr = stateAbbreviations[stateId];
          if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) {
            return; // Skip this state
          }

          if (annotationOffsets[stateId]) {
            const [dx, dy] = annotationOffsets[stateId];

            // Draw annotation line
            svg
              .append("line")
              .attr("x1", centroid[0])
              .attr("y1", centroid[1])
              .attr("x2", centroid[0] + dx)
              .attr("y2", centroid[1] + dy)
              .attr("stroke", "black")
              .attr("stroke-width", 1);

            // Add annotation text
            svg
              .append("text")
              .attr("x", centroid[0] + dx)
              .attr("y", centroid[1] + dy)
              .attr("font-size", "10px")
              .attr("font-weight", "bold")
              .attr("fill", "black")
              .text(abbr);
          } else {
            // Add direct text for larger states
            svg
              .append("text")
              .attr("x", centroid[0])
              .attr("y", centroid[1])
              .attr("font-size", "10px")
              .attr("font-weight", "bold")
              .attr("fill", "black")
              .text(abbr);
          }
        });
      }
    );
  }, [mutation, localMutation]);

  return <div ref={mapRef} id="chart-container"></div>;
};

export default Map;
